/**
 * Handling for display of form errors sent back from the server
 * @module lib/simpleFormErrors
 */

import scrollIntoView from 'scroll-into-view-if-needed';
import { whileMaintainingScroll } from './scroll';

/**
 * Selector for the error summary displayed at the top of the form
 */
const SELECTOR_GLOBAL_ERROR = '.js-form-errors';

/**
 * Selector for invalid fields
 */
const SELECTOR_INVALID_FIELDS = '.form-group-invalid';
/**
 * Selector for the error messages themselves
 */
const SELECTOR_ERROR_MESSAGE = '.invalid-feedback,.form-errors';
/**
 * Selector for the actual controls
 */
const SELECTOR_CONTROL = '.form-control';

/**
 * Attribute containing the field ID used to match the error messages
 * in the HTML returned from the server with the one already in the DOM
 */
const ATTR_FIELD_ID = 'data-field-id';

/**
 * Attribute for marking the field as `aria-invalid`
 */
const ATTR_ARIA_INVALID = 'aria-invalid';

const ATTR_ERROR_MESSAGE = 'data-form-errors-message';
const ATTR_ERROR_NO_HIGHLIGHT = 'data-form-errors-no-highlight';
const ATTR_ERROR_NOTIFY = 'data-form-errors-notify';
const ATTR_ERROR_PREPEND = 'data-form-errors-prepend';

/**
 * Class set to display the field as invalid
 */
const CLASS_CONTROL_INVALID = 'is-invalid';

export const EVENT_FORM_ERROR = 'form-error';

/**
 * Clears all errors from the form
 * @param {HTMLElement} form The element containing the form fields
 */
export function clearErrors(form) {
  Array.from(form.querySelectorAll(SELECTOR_ERROR_MESSAGE)).forEach(error =>
    error.parentNode.removeChild(error)
  );

  Array.from(form.querySelectorAll(SELECTOR_CONTROL)).forEach(control => {
    control.classList.remove(CLASS_CONTROL_INVALID);
    control.removeAttribute(ATTR_ARIA_INVALID);
  });

  form.removeAttribute('aria-invalid');
}

/**
 * Renders given `error` for the field with given `fieldId`
 * within the given `form`
 * @param {HTMLFormElement} form
 * @param {string} fieldId
 * @param {HTMLElement} error
 */
export function renderError(form, fieldId, error) {
  const field = form.querySelector(`[${ATTR_FIELD_ID}="${fieldId}"]`);
  if (field) {
    const existingError = field.querySelector(SELECTOR_ERROR_MESSAGE);
    if (existingError) {
      existingError.parentNode.replaceChild(error, existingError);
    } else {
      if (field.hasAttribute(ATTR_ERROR_PREPEND)) {
        field.insertBefore(error, field.children[0]);
      } else {
        // Errors are displayed after simple fields
        field.appendChild(error);
      }
    }
    if (!field.hasAttribute(ATTR_ERROR_NO_HIGHLIGHT)) {
      const control = field.querySelector(SELECTOR_CONTROL);
      if (control) {
        control.classList.add(CLASS_CONTROL_INVALID);
        control.setAttribute(ATTR_ARIA_INVALID, true);
      }
    }
    if (field.hasAttribute(ATTR_ERROR_NOTIFY)) {
      const event = new CustomEvent(EVENT_FORM_ERROR, { bubbles: true });
      field.dispatchEvent(event);
    }
  }
}

/**
 * Renders an error at the top of the form
 * @param {HTMLFormElement} form
 * @param {Object} options
 * @param {HTMLElement} [options.error=''] - The HTML element for the error
 * @param {boolean} options.focus - Focuses the error summary if true
 * @param {boolean} options.focusFirstError - Focuses the first error in the form if true
 * @param {boolean} [options.message=''] - The message of the error
 */
export function renderGlobalError(
  form,
  { error = '', focus, focusFirstError, message = '' } = {}
) {
  const globalError = form.querySelector(SELECTOR_GLOBAL_ERROR);
  globalError.classList.remove('bcs-widgetLayout__widget--ignore');

  globalError.innerHTML = getDisplayedError(globalError, { error, message });
  if (focus) {
    globalError.focus();
  }
  if (focusFirstError) {
    // The form validation sets some field to `:invalid` despite being valid
    // so using the `.is-invalid` as a prefered way to target field
    const firstError = form.querySelector(
      `
        .is-invalid:not([type="hidden"]):not(:disabled),
        [aria-invalid="true"]:not([type="hidden"]):not(:disabled)
      `
    );
    if (firstError) {
      whileMaintainingScroll(() => {
        firstError.focus();
      });
      scrollIntoView(firstError, { block: 'center', inline: 'center' });
    } else {
      globalError.focus();
    }
  }
}

/**
 * Gets the error to display in the given errorContainer. It can be:
 *  1. the provided error HTML element
 *  2. a `<div>` with the provided `message`
 *  3. a `<div>` with the message set in the `data-form-errors-message` attribute of the `errorContainer`
 * @param {HTMLElement} errorContainer - The element that'll contain the errors
 * @param {Object} options
 * @param {HTMLElement} [options.error] - The element to use as error message
 * @param {string} [options.message] - The message to render
 */
function getDisplayedError(errorContainer, { error, message }) {
  if (error) return error;
  const displayedMessage = message
    ? message
    : errorContainer.getAttribute(ATTR_ERROR_MESSAGE);
  return `<div class="form-errors">${displayedMessage}</div>`;
}

/**
 * Renders the given list of `<errors>` on the form
 * @param {HTMLElement} form The form into which to render the errors
 * @param {HTMLElement[]} errors The error elements to render in the form
 * @param {Object} options
 * @param {boolean} [options.focusFirstError] - Whether to focus the first error
 */
export function renderErrors(form, errors, { focusFirstError } = {}) {
  clearErrors(form);
  form.setAttribute('aria-invalid', true);
  Object.entries(errors.fields).forEach(([fieldId, error]) => {
    renderError(form, fieldId, error);
  });
  renderGlobalError(form, { error: errors.global, focusFirstError });
}

/**
 * Extract the errors from the given HTML sent back by the server
 * @param {HTMLElement} form
 */
export function extractErrors(form) {
  return {
    global: extractGlobalError(form),
    fields: extractFieldErrors(form)
  };
}

/**
 * Extracts the global error from the HTML sent back by the server
 * @param {HTMLElement} form
 */
export function extractGlobalError(form) {
  const globalError = form.querySelector(SELECTOR_GLOBAL_ERROR) || {};
  return globalError.innerHTML;
}

/**
 * Extract errors attached to specific fields from the HTML sent back by the server
 * @param {HTMLElement} form
 */
export function extractFieldErrors(form) {
  return Array.from(form.querySelectorAll(SELECTOR_INVALID_FIELDS)).reduce(
    (errors, invalidField) => {
      const error = invalidField.querySelector(SELECTOR_ERROR_MESSAGE);
      const fieldId = invalidField.getAttribute(ATTR_FIELD_ID);
      if (error && fieldId) {
        errors[fieldId] = error;
      }
      return errors;
    },
    {}
  );
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/lib/simpleFormErrors.js